import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UserPlus,
  Search,
  Award,
  TrendingUp,
  Users,
  Crown,
  Gift,
  Star,
  RefreshCw,
  Eye,
  Edit,
  X,
  ShoppingBag,
  Receipt,
  Phone,
  Mail,
  Heart,
  MapPin,
  CreditCard,
  Banknote,
  QrCode,
  Tag,
  Clock,
} from 'lucide-react';
import {
  useCustomer,
  useCustomerTransactions,
  useMembers,
  useLoyaltyAnalyticsOverview,
  useTiers,
  useBirthdayEligibleMembers,
  type LoyaltyMember,
} from '../../api/queries/loyalty.queries';
import { useCustomerOrders, type Order } from '../../api/queries/order.queries';
import { useQuickRegister } from '../../api/mutations/loyalty.mutations';
import { useAuthStore } from '../../stores/auth.store';
import MemberDetailModal from './components/MemberDetailModal';
import TierConfigModal from './components/TierConfigModal';

type TabType = 'operations' | 'overview' | 'members' | 'tiers' | 'birthdays';

function LoyaltyPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('operations');

  // Operations tab state
  const [showRegister, setShowRegister] = useState(false);
  const [searchPhone, setSearchPhone] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [regName, setRegName] = useState('');
  const [regPhone, setRegPhone] = useState('');

  // Order detail modal state
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Admin tabs state
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedMember, setSelectedMember] = useState<LoyaltyMember | null>(null);
  const [showMemberDetail, setShowMemberDetail] = useState(false);
  const [showTierConfig, setShowTierConfig] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);

  // Queries - Operations
  const { data: customerData, isLoading: isSearching } = useCustomer(selectedCustomerId);
  const { data: transactionsData } = useCustomerTransactions(selectedCustomerId);
  const { data: customerOrders } = useCustomerOrders(customerData?.customer?._id, 10);
  const registerMutation = useQuickRegister();

  // Queries - Admin
  const { data: analyticsData, refetch: refetchAnalytics } = useLoyaltyAnalyticsOverview();
  const { data: membersData, refetch: refetchMembers } = useMembers({
    page,
    limit: 25,
    tier: tierFilter || undefined,
  });
  const { data: tiers, refetch: refetchTiers } = useTiers();
  const { data: birthdayMembers } = useBirthdayEligibleMembers();

  const customer = customerData?.customer;
  const transactions = transactionsData?.transactions;

  const isAdmin = user?.role === 'HQ_ADMIN';
  const canViewAdmin = user?.role === 'HQ_ADMIN' || user?.role === 'HQ_PURCHASING';

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchPhone) {
      setSelectedCustomerId(searchPhone);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await registerMutation.mutateAsync({
        name: regName,
        phone: regPhone,
        branchId: user?.branchId || '',
      });
      setShowRegister(false);
      setSelectedCustomerId(result.member.phone);
      setSearchPhone(result.member.phone);
      setRegName('');
      setRegPhone('');
    } catch (error) {
      console.error('Failed to register customer:', error);
    }
  };

  const handleRefresh = () => {
    refetchAnalytics();
    refetchMembers();
    refetchTiers();
  };

  const handleViewMember = (member: LoyaltyMember) => {
    setSelectedMember(member);
    setShowMemberDetail(true);
  };

  const handleEditTier = (tier: string) => {
    setSelectedTier(tier);
    setShowTierConfig(true);
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'diamond':
        return 'text-sky-600 bg-sky-50 border-sky-200';
      case 'platinum':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'gold':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'silver':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-orange-600 bg-orange-50 border-orange-200';
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getBranchName = (order: Order) => {
    if (typeof order.branchId === 'object' && order.branchId) {
      return order.branchId.name || order.branchId.code || 'Unknown';
    }
    return 'Unknown';
  };

  const getPaymentIcon = (method: string) => {
    switch (method) {
      case 'cash':
        return <Banknote className="h-4 w-4" />;
      case 'card':
        return <CreditCard className="h-4 w-4" />;
      case 'qr':
        return <QrCode className="h-4 w-4" />;
      default:
        return <CreditCard className="h-4 w-4" />;
    }
  };

  // Build tabs based on user role
  const tabs = [
    { id: 'operations' as const, label: t('loyalty.operations') || 'Operations', icon: Heart },
    ...(canViewAdmin ? [
      { id: 'overview' as const, label: t('loyalty.overview'), icon: TrendingUp },
      { id: 'members' as const, label: t('loyalty.members'), icon: Users, count: membersData?.pagination.total },
      { id: 'tiers' as const, label: t('loyalty.tiers'), icon: Crown, count: tiers?.length },
      { id: 'birthdays' as const, label: t('loyalty.birthdays'), icon: Gift, count: birthdayMembers?.length },
    ] : []),
  ];

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('loyalty.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('loyalty.subtitle') || 'Manage customer loyalty points and rewards'}
            </p>
          </div>
          {canViewAdmin && activeTab !== 'operations' && (
            <button
              onClick={handleRefresh}
              className="p-2 rounded-lg hover:bg-secondary transition-colors"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b bg-card">
        <div className="flex gap-1 p-2 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-secondary'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {'count' in tab && tab.count !== undefined && tab.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  activeTab === tab.id
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Operations Tab - Modern Split Layout */}
        {activeTab === 'operations' && (
          <div className="h-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
              {/* Left Panel - Search */}
              <div className="space-y-4">
                {/* Search Card */}
                <div className="rounded-xl border bg-card p-4 shadow-sm">
                  <form onSubmit={handleSearch} className="flex gap-2">
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input
                        type="text"
                        value={searchPhone}
                        onChange={(e) => setSearchPhone(e.target.value)}
                        placeholder={t('loyalty.enterPhone') || 'Enter phone number...'}
                        className="w-full rounded-lg border bg-background pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSearching}
                      className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <Search className="h-4 w-4" />
                      {t('common.search')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowRegister(true)}
                      className="rounded-lg border bg-background px-4 py-2.5 text-sm font-medium hover:bg-secondary transition-colors flex items-center gap-2"
                      title={t('loyalty.register')}
                    >
                      <UserPlus className="h-4 w-4" />
                    </button>
                  </form>
                </div>

                {/* Customer Info Card */}
                {customer ? (
                  <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
                    {/* Customer Header */}
                    <div className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-xl font-bold text-primary">
                            {customer.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold truncate">{customer.name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getTierColor(customer.tier)}`}>
                              {t(`loyalty.${customer.tier}`)}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5">
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {customer.phone}
                            </span>
                            {customer.email && (
                              <span className="flex items-center gap-1 truncate">
                                <Mail className="h-3 w-3" />
                                {customer.email}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-3 divide-x border-b">
                      <div className="p-3 text-center">
                        <div className="text-2xl font-bold text-primary">{customer.currentPoints.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">{t('loyalty.points')}</div>
                      </div>
                      <div className="p-3 text-center">
                        <div className="text-2xl font-bold">฿{customer.lifetimeSpend.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">{t('loyalty.totalSpent') || 'Spent'}</div>
                      </div>
                      <div className="p-3 text-center">
                        <div className="text-2xl font-bold">{customer.totalOrders}</div>
                        <div className="text-xs text-muted-foreground">{t('loyalty.visits') || 'Visits'}</div>
                      </div>
                    </div>

                    {/* Points History - Compact */}
                    {transactions && transactions.length > 0 && (
                      <div className="p-3 border-b">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            {t('loyalty.recentTransactions') || 'Points Activity'}
                          </span>
                        </div>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {transactions.slice(0, 5).map((tx: { _id: string; type: string; createdAt: string; points: number }) => (
                            <div key={tx._id} className="flex items-center justify-between text-xs py-1">
                              <div className="flex items-center gap-2">
                                <span className={`w-1.5 h-1.5 rounded-full ${tx.type === 'earn' ? 'bg-green-500' : 'bg-red-500'}`} />
                                <span className="capitalize text-muted-foreground">{tx.type}</span>
                                <span className="text-muted-foreground/60">{formatDate(tx.createdAt)}</span>
                              </div>
                              <span className={`font-medium ${tx.type === 'earn' ? 'text-green-600' : 'text-red-600'}`}>
                                {tx.type === 'earn' ? '+' : '-'}{Math.abs(tx.points)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div className="p-3 bg-muted/30">
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium">Member since:</span> {customer.registeredAt ? formatDate(customer.registeredAt) : 'N/A'}
                      </div>
                    </div>
                  </div>
                ) : selectedCustomerId && !isSearching ? (
                  <div className="rounded-xl border bg-card p-8 text-center">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">{t('loyalty.customerNotFound') || 'Customer not found'}</p>
                    <button
                      onClick={() => {
                        setRegPhone(searchPhone);
                        setShowRegister(true);
                      }}
                      className="mt-3 text-sm text-primary hover:underline flex items-center gap-1 mx-auto"
                    >
                      <UserPlus className="h-4 w-4" />
                      {t('loyalty.registerNew') || 'Register as new customer'}
                    </button>
                  </div>
                ) : !selectedCustomerId ? (
                  <div className="rounded-xl border border-dashed bg-card/50 p-8 text-center">
                    <Search className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground text-sm">{t('loyalty.searchPrompt') || 'Search by phone number to view customer'}</p>
                  </div>
                ) : null}
              </div>

              {/* Right Panel - Order History */}
              <div className="space-y-4">
                {customer && customerOrders && customerOrders.length > 0 ? (
                  <div className="rounded-xl border bg-card shadow-sm h-full flex flex-col">
                    <div className="p-4 border-b flex items-center justify-between">
                      <h3 className="font-semibold flex items-center gap-2">
                        <ShoppingBag className="h-4 w-4" />
                        {t('loyalty.orderHistory') || 'Order History'}
                      </h3>
                      <span className="text-xs text-muted-foreground">{customerOrders.length} orders</span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2">
                      <div className="space-y-2">
                        {customerOrders.map((order) => (
                          <button
                            key={order._id}
                            onClick={() => setSelectedOrder(order)}
                            className="w-full text-left rounded-lg border bg-background p-3 hover:bg-secondary/50 hover:border-primary/30 transition-all cursor-pointer"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-sm">{order.orderNumber}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                    order.status === 'completed' ? 'bg-green-100 text-green-700' :
                                    order.status === 'cancelled' || order.status === 'voided' ? 'bg-red-100 text-red-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {order.status}
                                  </span>
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {order.items.map((item, idx) => (
                                    <span key={idx}>
                                      {item.quantity}x {item.menuItemName}
                                      {idx < order.items.length - 1 && ', '}
                                    </span>
                                  ))}
                                </div>
                                <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-1">
                                  <MapPin className="h-3 w-3" />
                                  <span>{getBranchName(order)}</span>
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="font-semibold">฿{order.total.toFixed(0)}</div>
                                <div className="text-[10px] text-muted-foreground">
                                  {formatDate(order.createdAt)}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : customer ? (
                  <div className="rounded-xl border border-dashed bg-card/50 p-8 text-center h-full flex flex-col items-center justify-center">
                    <Receipt className="h-10 w-10 text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground text-sm">{t('loyalty.noOrders') || 'No order history yet'}</p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed bg-card/50 p-8 text-center h-full flex flex-col items-center justify-center">
                    <ShoppingBag className="h-10 w-10 text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground text-sm">{t('loyalty.selectCustomerOrders') || 'Select a customer to view orders'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && analyticsData && (
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className="rounded-lg border bg-card p-6">
                <div className="flex items-center gap-3">
                  <Users className="h-10 w-10 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{analyticsData.totalMembers.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">{t('loyalty.totalMembers')}</p>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border bg-card p-6">
                <div className="flex items-center gap-3">
                  <Award className="h-10 w-10 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">{analyticsData.activeMembers.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">{t('loyalty.activeMembers')}</p>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border bg-card p-6">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-10 w-10 text-purple-500" />
                  <div>
                    <p className="text-2xl font-bold">{analyticsData.totalPointsIssued.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">{t('loyalty.totalPointsIssued')}</p>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border bg-card p-6">
                <div className="flex items-center gap-3">
                  <Star className="h-10 w-10 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold">{analyticsData.totalPointsRedeemed.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">{t('loyalty.totalPointsRedeemed')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tier Distribution */}
            <div className="rounded-lg border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">{t('loyalty.tierDistribution')}</h3>
                <p className="text-xs text-muted-foreground">{t('loyalty.clickToViewMembers') || 'Click to view members'}</p>
              </div>
              <div className="grid grid-cols-5 gap-4">
                {['bronze', 'silver', 'gold', 'platinum', 'diamond'].map((tier) => (
                  <button
                    key={tier}
                    onClick={() => {
                      setTierFilter(tier);
                      setActiveTab('members');
                      setPage(1);
                    }}
                    className={`p-4 rounded-lg border ${getTierColor(tier)} text-left hover:ring-2 hover:ring-primary/50 hover:shadow-md transition-all cursor-pointer`}
                  >
                    <p className="text-sm font-medium capitalize">{tier}</p>
                    <p className="text-2xl font-bold">
                      {analyticsData.tierDistribution[tier] || 0}
                    </p>
                    <p className="text-xs opacity-70">
                      {analyticsData.totalMembers > 0
                        ? `${Math.round(((analyticsData.tierDistribution[tier] || 0) / analyticsData.totalMembers) * 100)}%`
                        : '0%'}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border bg-card p-6">
                <h3 className="text-lg font-medium mb-2">{t('loyalty.recentSignups')}</h3>
                <p className="text-3xl font-bold text-green-600">{analyticsData.recentSignups}</p>
                <p className="text-sm text-muted-foreground">{t('loyalty.last7Days')}</p>
              </div>
              <div className="rounded-lg border bg-card p-6">
                <h3 className="text-lg font-medium mb-2">{t('loyalty.engagementRate')}</h3>
                <p className="text-3xl font-bold text-blue-600">{analyticsData.engagementRate}</p>
                <p className="text-sm text-muted-foreground">{t('loyalty.activeLast30Days')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="max-w-6xl mx-auto space-y-4">
            {/* Search and Filter */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  type="text"
                  value={memberSearchTerm}
                  onChange={(e) => setMemberSearchTerm(e.target.value)}
                  placeholder={t('loyalty.searchMembers')}
                  className="w-full rounded-lg border bg-background pl-10 pr-4 py-2"
                />
              </div>
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="rounded-lg border bg-background px-3 py-2"
              >
                <option value="">{t('common.allTiers') || 'All Tiers'}</option>
                <option value="bronze">Bronze</option>
                <option value="silver">Silver</option>
                <option value="gold">Gold</option>
                <option value="platinum">Platinum</option>
                <option value="diamond">Diamond</option>
              </select>
            </div>

            {/* Members Table */}
            <div className="rounded-lg border bg-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50">
                  <tr>
                    <th className="text-left py-3 px-4">{t('loyalty.member')}</th>
                    <th className="text-left py-3 px-4">{t('loyalty.contact')}</th>
                    <th className="text-center py-3 px-4">{t('loyalty.tier')}</th>
                    <th className="text-right py-3 px-4">{t('loyalty.points')}</th>
                    <th className="text-right py-3 px-4">{t('loyalty.totalSpent')}</th>
                    <th className="text-center py-3 px-4">{t('loyalty.status')}</th>
                    <th className="text-center py-3 px-4">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {membersData?.members.map((member) => (
                    <tr key={member._id} className="border-t hover:bg-secondary/30">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-xs text-muted-foreground">ID: {member.memberId}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <p>{member.phone}</p>
                        {member.email && (
                          <p className="text-xs text-muted-foreground">{member.email}</p>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getTierColor(member.tier)}`}>
                          {member.tier}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-medium">
                        {member.currentPoints.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        ฿{member.lifetimeSpend.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {member.isBanned ? (
                          <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-700">
                            {t('loyalty.banned')}
                          </span>
                        ) : member.isActive ? (
                          <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">
                            {t('loyalty.active')}
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-600">
                            {t('loyalty.inactive')}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => handleViewMember(member)}
                          className="p-1 hover:bg-secondary rounded"
                          title={t('common.view')}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {membersData && membersData.pagination.pages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {t('common.showing')} {((page - 1) * 25) + 1} - {Math.min(page * 25, membersData.pagination.total)} {t('common.of')} {membersData.pagination.total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 rounded border hover:bg-secondary disabled:opacity-50"
                  >
                    {t('common.previous')}
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= membersData.pagination.pages}
                    className="px-3 py-1 rounded border hover:bg-secondary disabled:opacity-50"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tiers Tab */}
        {activeTab === 'tiers' && (
          <div className="max-w-4xl mx-auto space-y-4">
            {tiers?.map((tier) => (
              <div key={tier._id} className={`rounded-lg border p-6 ${getTierColor(tier.tier)}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <Crown className="h-6 w-6" />
                      <h3 className="text-xl font-bold capitalize">{tier.tier}</h3>
                      {!tier.isActive && (
                        <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">
                          {t('common.inactive')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm mt-1">{tier.name.en}</p>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => handleEditTier(tier.tier)}
                      className="p-2 hover:bg-white/30 rounded"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-4 gap-4 mt-4">
                  <div>
                    <p className="text-xs opacity-70">{t('loyalty.minPoints')}</p>
                    <p className="font-bold">{tier.minLifetimePoints.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs opacity-70">{t('loyalty.earnRate')}</p>
                    <p className="font-bold">{tier.earnRate} pts/฿</p>
                  </div>
                  <div>
                    <p className="text-xs opacity-70">{t('loyalty.earnMultiplier')}</p>
                    <p className="font-bold">{tier.earnMultiplier}x</p>
                  </div>
                  <div>
                    <p className="text-xs opacity-70">{t('loyalty.birthdayBonus')}</p>
                    <p className="font-bold">{tier.birthdayBonus.toLocaleString()} pts</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {tier.prioritySupport && (
                    <span className="px-2 py-1 rounded text-xs bg-white/30">{t('loyalty.prioritySupport')}</span>
                  )}
                  {tier.exclusivePromotions && (
                    <span className="px-2 py-1 rounded text-xs bg-white/30">{t('loyalty.exclusivePromotions')}</span>
                  )}
                  {tier.freeDelivery && (
                    <span className="px-2 py-1 rounded text-xs bg-white/30">{t('loyalty.freeDelivery')}</span>
                  )}
                  {tier.birthdayFreeItem && (
                    <span className="px-2 py-1 rounded text-xs bg-white/30">{t('loyalty.birthdayFreeItem')}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Birthdays Tab */}
        {activeTab === 'birthdays' && (
          <div className="max-w-4xl mx-auto">
            <div className="rounded-lg border bg-card">
              <div className="p-4 border-b">
                <h3 className="font-medium">{t('loyalty.birthdayEligible')}</h3>
                <p className="text-sm text-muted-foreground">{t('loyalty.birthdayEligibleDesc')}</p>
              </div>
              {birthdayMembers && birthdayMembers.length > 0 ? (
                <div className="divide-y">
                  {birthdayMembers.map((member) => (
                    <div key={member._id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Gift className="h-8 w-8 text-pink-500" />
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {member.phone} • {member.birthdate && formatDate(member.birthdate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs capitalize ${getTierColor(member.tier)}`}>
                          {member.tier}
                        </span>
                        <button
                          onClick={() => handleViewMember(member)}
                          className="px-3 py-1 rounded text-sm bg-pink-100 text-pink-700 hover:bg-pink-200"
                        >
                          {t('loyalty.viewProfile')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  <Gift className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>{t('loyalty.noBirthdays')}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Register Modal */}
      {showRegister && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-md rounded-lg bg-card">
            <div className="border-b p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">{t('loyalty.register')}</h2>
              <button
                onClick={() => setShowRegister(false)}
                className="rounded-full p-2 hover:bg-secondary"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleRegister} className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('loyalty.customerName') || 'Name'}</label>
                <input
                  type="text"
                  value={regName}
                  onChange={(e) => setRegName(e.target.value)}
                  required
                  className="w-full rounded-lg border bg-background px-3 py-2"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('loyalty.phone') || 'Phone'}</label>
                <input
                  type="tel"
                  value={regPhone}
                  onChange={(e) => setRegPhone(e.target.value)}
                  required
                  className="w-full rounded-lg border bg-background px-3 py-2"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowRegister(false)}
                  className="flex-1 rounded-lg border bg-background px-4 py-2 hover:bg-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={registerMutation.isPending}
                  className="flex-1 rounded-lg bg-primary px-4 py-2 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {registerMutation.isPending ? t('common.loading') : t('common.confirm')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Member Detail Modal */}
      {showMemberDetail && selectedMember && (
        <MemberDetailModal
          member={selectedMember}
          onClose={() => {
            setShowMemberDetail(false);
            setSelectedMember(null);
          }}
          onRefresh={() => refetchMembers()}
        />
      )}

      {/* Tier Config Modal */}
      {showTierConfig && (
        <TierConfigModal
          tier={selectedTier}
          onClose={() => {
            setShowTierConfig(false);
            setSelectedTier(null);
          }}
          onSuccess={() => {
            setShowTierConfig(false);
            setSelectedTier(null);
            refetchTiers();
          }}
        />
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => e.target === e.currentTarget && setSelectedOrder(null)}
        >
          <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-card shadow-xl">
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-card p-4 rounded-t-xl">
              <div>
                <h2 className="text-lg font-semibold">{selectedOrder.orderNumber}</h2>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  <span>{getBranchName(selectedOrder)}</span>
                  <span>•</span>
                  <Clock className="h-3 w-3" />
                  <span>{formatDateTime(selectedOrder.createdAt)}</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="rounded-full p-2 hover:bg-secondary transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedOrder.status === 'completed' ? 'bg-green-100 text-green-700' :
                  selectedOrder.status === 'cancelled' || selectedOrder.status === 'voided' ? 'bg-red-100 text-red-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>
                  {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1).replace('_', ' ')}
                </span>
                {selectedOrder.dailyNumber && (
                  <span className="text-sm text-muted-foreground">
                    Daily #{selectedOrder.dailyNumber}
                  </span>
                )}
              </div>

              {/* Order Items */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  {t('pos.items')}
                </h3>
                <div className="rounded-lg border divide-y">
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="p-3">
                      <div className="flex justify-between">
                        <div className="flex-1">
                          <div className="font-medium">{item.quantity}x {item.menuItemName}</div>
                          <div className="text-xs text-muted-foreground mt-0.5 space-x-2">
                            <span>Size: {item.size}</span>
                            {item.sweetness && <span>• Sweet: {item.sweetness}%</span>}
                          </div>
                          {item.modifiers && item.modifiers.length > 0 && (
                            <div className="text-xs text-muted-foreground mt-1">
                              + {item.modifiers.map(m => m.name).join(', ')}
                            </div>
                          )}
                          {item.notes && (
                            <div className="text-xs text-orange-600 mt-1">
                              Note: {item.notes}
                            </div>
                          )}
                        </div>
                        <div className="text-right font-medium">
                          ฿{item.totalPrice.toFixed(0)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Summary */}
              <div className="rounded-lg bg-secondary/50 p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('pos.subtotal')}</span>
                  <span>฿{selectedOrder.subtotal.toFixed(2)}</span>
                </div>
                {selectedOrder.discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span className="flex items-center gap-1">
                      <Tag className="h-3 w-3" />
                      {t('pos.discount')}
                    </span>
                    <span>-฿{selectedOrder.discount.toFixed(2)}</span>
                  </div>
                )}
                {selectedOrder.tax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('pos.tax')}</span>
                    <span>฿{selectedOrder.tax.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                  <span>{t('pos.total')}</span>
                  <span>฿{selectedOrder.total.toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Methods */}
              {selectedOrder.payments && selectedOrder.payments.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    {t('pos.payment')}
                  </h3>
                  <div className="rounded-lg border divide-y">
                    {selectedOrder.payments.map((payment, idx) => (
                      <div key={idx} className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getPaymentIcon(payment.method)}
                          <span className="capitalize">{payment.method}</span>
                          {payment.reference && (
                            <span className="text-xs text-muted-foreground">({payment.reference})</span>
                          )}
                        </div>
                        <span className="font-medium">฿{payment.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Info */}
              <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
                {selectedOrder.createdBy && typeof selectedOrder.createdBy === 'object' && (
                  <div>
                    <span className="font-medium">Created by:</span> {selectedOrder.createdBy.username}
                  </div>
                )}
                {selectedOrder.completedAt && (
                  <div>
                    <span className="font-medium">Completed:</span> {formatDateTime(selectedOrder.completedAt)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoyaltyPage;
